import React from 'react'
import { UserService } from '@duck/api-definitions'
import { MultiPick } from '@duck/ui'
import { RelativeDateTime } from '../datetime/RelativeDateTime'
import { TranslationKey } from '@/modules/i18n/types'
import { useTar } from '@/modules'

interface UserFormProps {
  formData: Partial<Omit<UserService.User, 'PersonalInfo'>>
  onChange: (field: keyof UserService.User, value: string | boolean | string[]) => void
  onSave: () => void
  onCancel: () => void
  isSaving?: boolean
  saveError?: string | null
  isEditing?: boolean
  isReadOnly?: boolean
  allAccessGroups: string[]
  fieldErrors?: Record<string, string>
}


const ROLE_OPTIONS = [
  { value: 'user', label: 'common.user' },
  { value: 'partner', label: 'common.partner' },
  { value: 'admin', label: 'common.admin' },
] as const

const BADGE_OPTIONS: { value: UserService.UserBadge; label: TranslationKey }[] = [
  { value: UserService.UserBadge.AssignmentOnly, label: 'users.assignmentOnly' },
  { value: UserService.UserBadge.AlphaTester, label: 'users.alphaTester' },
  { value: UserService.UserBadge.HardcoreUser, label: 'users.hardcoreUser' },
] as const

export const UserForm: React.FC<UserFormProps> = ({
  formData,
  onChange,
  onSave,
  onCancel,
  isSaving = false,
  saveError,
  isEditing = false,
  isReadOnly = false,
  allAccessGroups,
  fieldErrors = {}
}) => {
  const { t } = useTar()

  const renderFormField = (
    label: TranslationKey,
    field: keyof Omit<UserService.User, 'PersonalInfo'>,
    type: 'text' | 'email' | 'select' | 'checkbox-group' | 'checkbox' = 'text',
    required = false,
    options?: readonly { value: string; label: TranslationKey }[],
    disabled = false
  ) => {
    const value = formData[field] || (type === 'checkbox-group' ? [] : type === 'checkbox' ? false : '')
    const baseInputClasses = `
      w-full px-3 py-2 text-sm border border-border rounded-md 
      bg-card text-foreground placeholder-secondary/60
      focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent
      transition-colors
      ${disabled ? 'bg-secondary/5 text-muted-foreground cursor-not-allowed' : 'hover:border-secondary'}
    `

    // Render read-only text when in read-only mode
    if (isReadOnly) {
      return (
        <div className="space-y-2">
          <label className="block text-sm font-medium text-muted-foreground">
            {t(label)}
          </label>
          {type === 'checkbox-group' && options ? (
            <div className="text-foreground">
              {Array.isArray(value) && value.length > 0
                ? options.filter(opt => value.includes(opt.value as string))
                  .map(opt => t(opt.label)).join(', ')
                : t('common.none')
              }
            </div>
          ) : type === 'select' && options ? (
            <div className="text-foreground">
              {t(options.find(opt => opt.value === value)?.label || 'common.na')}
            </div>
          ) : type === 'checkbox' ? (
            <div className="text-foreground">
              {value ? t('common.yes') : t('common.no')}
            </div>
          ) : (
            <div className="text-foreground">
              {value.toString() || t('common.na')}
            </div>
          )}
        </div>
      )
    }

    return (
      <div className="space-y-2">
        <label className="block text-sm font-medium text-foreground">
          {t(label)}
          {required && <span className="text-error ms-1">*</span>}
        </label>
        {type === 'checkbox-group' && options ? (
          <div className="space-y-2">
            {options.map((option) => {
              const isChecked = Array.isArray(value) && value.includes(option.value as string)
              return (
                <label key={option.value} className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={isChecked}
                    onChange={(e) => {
                      const currentArray = Array.isArray(value) ? value : []
                      if (e.target.checked) {
                        onChange(field, [...currentArray, option.value])
                      } else {
                        onChange(field, currentArray.filter(v => v !== option.value))
                      }
                    }}
                    disabled={disabled}
                    className="h-4 w-4 text-accent focus:ring-accent border-border rounded"
                  />
                  <span className="text-sm text-foreground">{t(option.label)}</span>
                </label>
              )
            })}
          </div>
        ) : type === 'checkbox' ? (
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={value as boolean}
              onChange={(e) => onChange(field, e.target.checked)}
              disabled={disabled}
              className="h-4 w-4 text-accent focus:ring-accent border-border rounded"
            />
            <span className="text-sm text-foreground">{t('users.enableThisSetting')}</span>
          </label>
        ) : type === 'select' && options ? (
          <select
            value={value as string}
            onChange={(e) => onChange(field, e.target.value)}
            disabled={disabled}
            className={baseInputClasses}
          >
            <option value="">{t('common.select')} {t(label).toLowerCase()}</option>
            {options.map((option) => (
              <option key={option.value} value={option.value}>
                {t(option.label)}
              </option>
            ))}
          </select>
        ) : (
          <input
            type={type === 'email' ? 'email' : 'text'}
            value={value as string}
            onChange={(e) => onChange(field, e.target.value)}
            disabled={disabled || (isEditing && field === 'email')}
            className={baseInputClasses}
            placeholder={t('common.enterValue', { value: t(label).toLowerCase() })}
          />
        )}
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Basic Information */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium text-foreground border-b border-border pb-2">
          {t('common.basicInformation')}
        </h3>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="lg:col-span-2">
            {renderFormField('common.emailAddress', 'email', 'email', true, undefined, isEditing)}
          </div>
          <div className="lg:col-span-1">
            {renderFormField('common.firstName', 'firstName', 'text', true)}
          </div>
          <div className="lg:col-span-1">
            {renderFormField('common.lastName', 'lastName', 'text', true)}
          </div>
          <div className="lg:col-span-1">
            {renderFormField('common.role', 'role', 'select', true, ROLE_OPTIONS)}
          </div>
          <div className="lg:col-span-1">
            {renderFormField('organizations.organizationId', 'organizationId', 'text', false)}
          </div>
          <div className="lg:col-span-1">
            <div className="space-y-2">
              <label className="block text-sm font-medium text-foreground">
                {t('common.accessGroups')}
                <span className="text-error ms-1">*</span>
              </label>
              {isReadOnly ? (
                <div className="text-foreground">
                  {Array.isArray(formData.accessGroups) && formData.accessGroups.length > 0
                    ? formData.accessGroups.join(', ')
                    : t('common.none')
                  }
                </div>
              ) : (
                <>
                  <MultiPick
                    options={allAccessGroups.map((g) => ({ label: g, value: g }))}
                    selected={Array.isArray(formData.accessGroups) ? (formData.accessGroups as string[]) : []}
                    onChange={(vals) => onChange('accessGroups', vals)}
                    placeholder={t('common.pickAccessGroups')}
                    className='bg-card'
                    allowNewItem
                  />
                  {fieldErrors.accessGroups && (
                    <p className="text-sm text-error mt-1">{fieldErrors.accessGroups}</p>
                  )}
                </>
              )}
            </div>
          </div>
          <div className="lg:col-span-2">
            {renderFormField('common.notes', 'notes', 'text', false)}
          </div>
          <div className="lg:col-span-2">
            <div className="space-y-2">
              <label className="block text-sm font-medium text-foreground">
                {t('users.badges')}
              </label>
              {isReadOnly ? (
                <div className="text-foreground">
                  {Array.isArray(formData.badges) && formData.badges.length > 0
                    ? formData.badges.map((b) => t(BADGE_OPTIONS.find((o) => o.value === b)?.label ?? 'common.none')).join(', ')
                    : t('common.none')}
                </div>
              ) : (
                <MultiPick
                  options={BADGE_OPTIONS}
                  selected={Array.isArray(formData.badges) ? formData.badges : []}
                  onChange={(vals) => onChange('badges', vals)}
                  placeholder={t('common.select')}
                  className="bg-card"
                />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Account Status */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium text-foreground border-b border-border pb-2">
          {t('users.accountStatus')}
        </h3>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="lg:col-span-1">
            {renderFormField('common.isActive', 'isActive', 'checkbox', false)}
          </div>
          <div className="lg:col-span-1">
            {renderFormField('common.isVerified', 'isVerified', 'checkbox', false)}
          </div>
        </div>
      </div>

      {/* User Details (Read-only for editing) */}
      {isEditing && formData.id && (
        <div className="space-y-4">
          <h3 className="text-lg font-medium text-foreground border-b border-border pb-2">
            {t('users.userDetails')}
          </h3>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 text-sm">
            <div className="space-y-2">
              <label className="block font-medium text-muted-foreground">{t('common.id')}</label>
              <p className="text-foreground font-mono">{formData.id}</p>
            </div>
            <div className="space-y-2">
              <label className="block font-medium text-muted-foreground">{t('common.invitationId')}</label>
              <p className="text-foreground">{formData.invitationId || t('common.notApplicable')}</p>
            </div>
            <div className="space-y-2">
              <label className="block font-medium text-muted-foreground">{t('common.createdAt')}</label>
              <p className="text-foreground">
                {formData.createdAt ? (
                  <RelativeDateTime isoDateString={formData.createdAt} />
                ) : t('common.na')}
              </p>
            </div>
            <div className="space-y-2">
              <label className="block font-medium text-muted-foreground">{t('common.updatedAt')}</label>
              <p className="text-foreground">
                {formData.updatedAt ? (
                  <RelativeDateTime isoDateString={formData.updatedAt} />
                ) : t('common.na')}
              </p>
            </div>
            <div className="space-y-2">
              <label className="block font-medium text-muted-foreground">{t('common.lastLoginAt')}</label>
              <p className="text-foreground">
                {formData.lastLoginAt ? (
                  <RelativeDateTime isoDateString={formData.lastLoginAt} />
                ) : t('common.never')}
              </p>
            </div>
            <div className="space-y-2">
              <label className="block font-medium text-muted-foreground">{t('common.avatar')}</label>
              <p className="text-foreground">{formData.avatar ? t('common.hasAvatar') : t('common.noAvatar')}</p>
            </div>
          </div>
        </div>
      )}

      {/* Error Display */}
      {saveError && (
        <div className="p-3 bg-error/10 border border-error/20 rounded-md">
          <p className="text-sm text-error">{saveError}</p>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row gap-3 pt-6 border-t border-border justify-end">
        <button
          onClick={onCancel}
          disabled={isSaving}
          className="px-4 py-2 text-sm font-medium text-muted-foreground border border-border rounded-md hover:bg-secondary/5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isReadOnly ? t('common.close') : t('common.cancel')}
        </button>
        {!isReadOnly && (
          <button
            onClick={onSave}
            disabled={isSaving}
            className="px-4 py-2 bg-accent text-white rounded-md text-sm font-medium hover:bg-accent/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {isSaving && (
              <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
            )}
            {isSaving ? t('common.saving') : isEditing ? t('users.updateUser') : t('users.createUser')}
          </button>
        )}
      </div>
    </div>
  )
}
